import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Shield, Lock, Users, Zap, BarChart3, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import SEOHead from '@/components/SEOHead';
import { useLanguagePrefix } from '@/hooks/useLanguagePrefix';

const DirectReservePage = () => {
  const { t } = useTranslation();
  const langPrefix = useLanguagePrefix();
  const benefits = [
    'Easy access to physical and digital precious metals products',
    'Integrated procurement, storage and trading services',
    'Enhanced visibility and control over transactions',
    'Bespoke global distribution services',
    'Seamless API integration and interoperability',
    'Professional trade desk, marketing and sales support'
  ];

  const badges = ['AUDITED', 'FULL-SCALE RETAIL SERVICES', 'CENTRALIZED CONTROL', 'INTEGRATED EXPERIENCE'];

  // Use translated FAQs
  const faqs = [
    { question: t('directReserve.faq.q1.question'), answer: t('directReserve.faq.q1.answer') },
    { question: t('directReserve.faq.q2.question'), answer: t('directReserve.faq.q2.answer') },
    { question: t('directReserve.faq.q3.question'), answer: t('directReserve.faq.q3.answer') },
    { question: t('directReserve.faq.q4.question'), answer: t('directReserve.faq.q4.answer') },
    { question: t('directReserve.faq.q5.question'), answer: t('directReserve.faq.q5.answer') },
    { question: t('directReserve.faq.q6.question'), answer: t('directReserve.faq.q6.answer') },
    { question: t('directReserve.faq.q7.question'), answer: t('directReserve.faq.q7.answer') },
    { question: t('directReserve.faq.q8.question'), answer: t('directReserve.faq.q8.answer') },
    { question: t('directReserve.faq.q9.question'), answer: t('directReserve.faq.q9.answer') }
  ];

  return (
    <div className="min-h-screen" data-testid="direct-reserve-page">
      <SEOHead 
        title={t('seo.directReserve.title')}
        description={t('seo.directReserve.description')}
        keywords="digital gold, KDRGOLD, 1:1 gold tokenization, LBMA compliant, redeemable gold, digital silver, KDRSILVER, redeemable silver, blockchain gold trading, on-chain metal ownership, Fireblocks custody, ERC-3643, tokenization API, RWA, real world assets"
        canonical={`${langPrefix}/direct-reserve`}
      />
      
      {/* Product Structured Data for KDRGOLD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "KDRGOLD (Digital Gold)",
          "brand": {
            "@type": "Brand",
            "name": "Kitco DirectReserve"
          },
          "description": "Tokenized gold fully backed 1:1 by physical gold held at Kitco DR Vault; audited and redeemable with LBMA compliance.",
          "sku": "KDRGOLD",
          "category": "Investment",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "USD",
            "price": "Spot"
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Backing",
              "value": "1:1 physical gold"
            },
            {
              "@type": "PropertyValue",
              "name": "Vault",
              "value": "Kitco DR Vault (government depositories)"
            },
            {
              "@type": "PropertyValue",
              "name": "Audits",
              "value": "Independent third-party attestations"
            },
            {
              "@type": "PropertyValue",
              "name": "Standard",
              "value": "ERC-3643 compliant"
            }
          ]
        })}
      </script>
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center text-white py-32"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), url(https://images.unsplash.com/photo-1624365169364-0640dd10e180)'
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-kitco-gold font-semibold mb-4 tracking-wide" data-testid="hero-label">{t('directReserve.title')}</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="hero-title">
            {t('directReserve.hero.title')}
          </h1>
          <p className="text-xl text-grey-200 mb-8 max-w-3xl">
            {t('directReserve.hero.subtitle')}
          </p>
          <Link to={`${langPrefix}/contact`}>
            <Button size="lg" className="bg-kitco-gold hover:bg-kitco-gold-hover text-black font-semibold" data-testid="hero-cta-button">
              {t('nav.contact')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* About / Platform Details Section */}
      <section className="py-20 bg-grey-50" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            
            <p className="text-lg text-grey-600 leading-relaxed mb-6" data-testid="about-content">
              {t('directReserve.about.paragraph1')}
            </p>
            
            <p className="text-lg text-grey-600 leading-relaxed">
              {t('directReserve.about.paragraph2')}
            </p>
            
            <div className="mt-8 p-4 bg-amber-50 border-l-4 border-kitco-gold rounded-r">
              <p className="text-sm text-grey-500">
                <strong>Roadmap:</strong> {t('directReserve.about.roadmap')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-slate-50" data-testid="key-features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-kitco-navy mb-4 text-center" data-testid="features-title">{t('directReserve.keyFeatures.title')}</h2>
          <div className="w-24 h-1 bg-kitco-gold mx-auto mb-12"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-testid="features-list">
            <div className="flex items-start bg-grey-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <CheckCircle2 className="h-6 w-6 text-kitco-gold mr-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-grey-600" dangerouslySetInnerHTML={{ __html: t('directReserve.keyFeatures.feature1') }}></p>
              </div>
            </div>

            <div className="flex items-start bg-grey-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <CheckCircle2 className="h-6 w-6 text-kitco-gold mr-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-grey-600" dangerouslySetInnerHTML={{ __html: t('directReserve.keyFeatures.feature2') }}></p>
              </div>
            </div>

            <div className="flex items-start bg-grey-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <CheckCircle2 className="h-6 w-6 text-kitco-gold mr-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-grey-600" dangerouslySetInnerHTML={{ __html: t('directReserve.keyFeatures.feature3') }}></p>
              </div>
            </div>

            <div className="flex items-start bg-grey-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <CheckCircle2 className="h-6 w-6 text-kitco-gold mr-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-grey-600" dangerouslySetInnerHTML={{ __html: t('directReserve.keyFeatures.feature4') }}></p>
              </div>
            </div>

            <div className="flex items-start bg-grey-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <CheckCircle2 className="h-6 w-6 text-kitco-gold mr-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-grey-600" dangerouslySetInnerHTML={{ __html: t('directReserve.keyFeatures.feature5') }}></p>
              </div>
            </div>

            <div className="flex items-start bg-grey-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <CheckCircle2 className="h-6 w-6 text-kitco-gold mr-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-grey-600" dangerouslySetInnerHTML={{ __html: t('directReserve.keyFeatures.feature6') }}></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rethinking Precious Metals Section */}
      <section className="py-20 bg-gradient-to-br from-kitco-teal via-kitco-teal-hover to-grey-800 relative overflow-hidden" data-testid="rethinking-section">
        {/* Large Faded Icon Background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <svg className="w-[800px] h-[800px] text-kitco-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="0.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-fade-in" data-testid="rethinking-title" dangerouslySetInnerHTML={{ __html: t('directReserve.rethinking.title') }}>
            </h2>
            <p className="text-2xl text-white/90" data-testid="rethinking-subtitle">
              {t('directReserve.rethinking.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
            {/* Full Asset Integrity */}
            <div className="group bg-kitco-navy/80 backdrop-blur-sm p-8 rounded-xl border border-kitco-gold/30 hover:border-kitco-gold transition-all duration-300 hover:shadow-2xl hover:shadow-kitco-gold/30 hover:-translate-y-2" data-testid="feature-01">
              <div className="flex items-center mb-4">
                <Shield className="h-12 w-12 text-kitco-gold mr-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('directReserve.rethinking.integrity.title')}</h3>
              </div>
              <p className="text-grey-200 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: t('directReserve.rethinking.integrity.description') }}>
              </p>
              <p className="text-grey-300 text-sm">{t('directReserve.rethinking.integrity.note')}</p>
            </div>

            {/* Instant Trading & Settlement */}
            <div className="group bg-kitco-navy/80 backdrop-blur-sm p-8 rounded-xl border border-kitco-gold/30 hover:border-kitco-gold transition-all duration-300 hover:shadow-2xl hover:shadow-kitco-gold/30 hover:-translate-y-2" data-testid="feature-02">
              <div className="flex items-center mb-4">
                <Zap className="h-12 w-12 text-kitco-gold mr-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('directReserve.rethinking.instant.title')}</h3>
              </div>
              <p className="text-grey-200 leading-relaxed mb-4">
                {t('directReserve.rethinking.instant.description')}
              </p>
              <p className="text-grey-300 text-sm">{t('directReserve.rethinking.instant.note')}</p>
            </div>

            {/* Transparent & Auditable */}
            <div className="group bg-kitco-navy/80 backdrop-blur-sm p-8 rounded-xl border border-kitco-gold/30 hover:border-kitco-gold transition-all duration-300 hover:shadow-2xl hover:shadow-kitco-gold/30 hover:-translate-y-2" data-testid="feature-03">
              <div className="flex items-center mb-4">
                <BarChart3 className="h-12 w-12 text-kitco-gold mr-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('directReserve.rethinking.transparent.title')}</h3>
              </div>
              <p className="text-grey-300 leading-relaxed mb-4">
                {t('directReserve.rethinking.transparent.description')}
              </p>
              <p className="text-grey-300 text-sm">{t('directReserve.rethinking.transparent.note')}</p>
            </div>

            {/* Regulated Digital Infrastructure */}
            <div className="group bg-kitco-navy p-8 rounded-xl border border-kitco-gold/20 hover:border-kitco-gold transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/20 hover:-translate-y-2" data-testid="feature-04">
              <div className="flex items-center mb-4">
                <Lock className="h-12 w-12 text-kitco-gold mr-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('directReserve.rethinking.regulated.title')}</h3>
              </div>
              <p className="text-grey-300 leading-relaxed mb-4">
                {t('directReserve.rethinking.regulated.description')}
              </p>
              <p className="text-grey-300 text-sm">{t('directReserve.rethinking.regulated.note')}</p>
            </div>

            {/* 24/7 Trading */}
            <div className="group bg-kitco-navy p-8 rounded-xl border border-kitco-gold/20 hover:border-kitco-gold transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/20 hover:-translate-y-2" data-testid="feature-05">
              <div className="flex items-center mb-4">
                <Globe className="h-12 w-12 text-kitco-gold mr-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('directReserve.rethinking.trading247.title')}</h3>
              </div>
              <p className="text-grey-300 leading-relaxed mb-4">
                {t('directReserve.rethinking.trading247.description')}
              </p>
              <p className="text-grey-300 text-sm">{t('directReserve.rethinking.trading247.note')}</p>
            </div>

            {/* Trusted Global Ecosystem */}
            <div className="group bg-kitco-navy p-8 rounded-xl border border-kitco-gold/20 hover:border-kitco-gold transition-all duration-300 hover:shadow-2xl hover:shadow-amber-500/20 hover:-translate-y-2" data-testid="feature-06">
              <div className="flex items-center mb-4">
                <Users className="h-12 w-12 text-kitco-gold mr-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-2xl font-bold text-white group-hover:text-kitco-gold transition-colors">{t('directReserve.rethinking.ecosystem.title')}</h3>
              </div>
              <p className="text-grey-300 leading-relaxed mb-4">
                {t('directReserve.rethinking.ecosystem.description')}
              </p>
              <p className="text-grey-300 text-sm">{t('directReserve.rethinking.ecosystem.note')}</p>
            </div>
          </div>

        </div>
      </section>

      {/* Why Choose DirectReserve - Extended Benefits - Hidden for cleaner look */}

      {/* Ecosystem Participants - Hidden for now */}
      {/* <section className="py-20 bg-slate-50" data-testid="ecosystem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-kitco-navy mb-4 text-center">Ecosystem Participants</h2>
          <div className="w-24 h-1 bg-kitco-gold mx-auto mb-12"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">External Sub-Custodians</h3>
              <p className="text-grey-600 text-sm">COMEX depositories or Royal Canadian Mint facilities approved by KDRV for sovereign-grade storage compliance.</p>
            </div>

            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">Kitco Platform Operations</h3>
              <p className="text-grey-600 text-sm">Manages onboarding, platform rules, settlement, and reconciliation across the ecosystem.</p>
            </div>

            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">The Network Firm</h3>
              <p className="text-grey-600 text-sm">Independent attestation provider verifying 1:1 token-to-metal correspondence across the platform.</p>
            </div>

            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">Fireblocks</h3>
              <p className="text-grey-600 text-sm">Provides secure multi-party computation (MPC) wallet infrastructure for institutional token custody and transfers.</p>
            </div>

            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">DirectReserve Ledger</h3>
              <p className="text-grey-600 text-sm">ERC-3643 permissioned ledger used to record legal title to tokenized metal. Operated by Kitco DR Vault operations.</p>
            </div>

            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">Kitco DR Vault (KDRV)</h3>
              <p className="text-grey-600 text-sm">Legal custodian and vault operator responsible for physical metal storage, ledger updates, and token issuance control.</p>
            </div>

            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">Custodial Participant (CP)</h3>
              <p className="text-grey-600 text-sm">Authorized entity with an account at Kitco DR Vault to custody tokenized metals on behalf of clients or proprietary books.</p>
            </div>

            <div className="bg-grey-50 p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-lg text-kitco-gold-hover mb-3">Metals Dealer</h3>
              <p className="text-grey-600 text-sm">May act as a Custodial Participant or service multiple CPs by onboarding end users and facilitating trading.</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* Attestation Reports Section - Hidden for now */}
      {/* <section className="py-20 bg-grey-50" data-testid="attestation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-kitco-navy mb-8" data-testid="attestation-title">Attestation Reports</h2>
          <p className="text-lg text-grey-600 leading-relaxed mb-6" data-testid="attestation-content">
            Kitco recognizes the need for a high standard of public transparency for consumers and businesses to build trust. As a result, we have engaged Cohen & Co., a nationally ranked accounting firm, to conduct monthly attestations on our Management Report.
          </p>
          <p className="text-lg text-grey-600 leading-relaxed mb-6">
            Every attestation that has been published since the launch of DirectReserve™ can be viewed on our{' '}
            <Link to="/attestations" className="text-kitco-gold-hover hover:text-amber-700 font-semibold" data-testid="attestation-link">
              Attestations page
            </Link>.
          </p>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="py-20 bg-slate-50" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-kitco-navy mb-12 text-center" data-testid="faq-title">{t('home.faq.title')}</h2>
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-grey-50 border border-gray-200 rounded-lg px-6" data-testid={`faq-item-${index}`}>
                <AccordionTrigger className="text-left font-semibold text-kitco-navy hover:text-kitco-gold-hover" data-testid={`faq-question-${index}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-grey-600" data-testid={`faq-answer-${index}`}>
                  <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-kitco-navy text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6" data-testid="cta-title">{t('directReserve.cta.title')}</h2>
          <p className="text-xl text-grey-300 mb-8" data-testid="cta-subtitle">
            {t('directReserve.cta.description')}
          </p>
          <Link to={`${langPrefix}/contact`}>
            <Button size="lg" className="bg-kitco-gold hover:bg-kitco-gold text-black font-semibold" data-testid="cta-button">
              {t('directReserve.cta.button')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DirectReservePage;